import type { Dispatch } from "react";
import type { EditorAction } from "../editor/reducers";
import type { RegionTileSwitchFragment } from "../client/generated";
import { matchRegionType } from "../types/utils";
import { ArtifactRegionTile } from "../types/artifact/ArtifactRegionTile";
import { CaptionRegionTile } from "../types/caption/CaptionRegionTile";
import { FigureRegionTile } from "../types/figure/FigureRegionTile";
import { FormulaRegionTile } from "../types/formula/FormulaRegionTile";
import { HeadingRegionTile } from "../types/heading/HeadingRegionTile";
import { HybridRegionTile } from "../types/hybrid/HybridRegionTile";
import { ImageRegionTile } from "../types/image/ImageRegionTile";
import { ListRegionTile } from "../types/list/ListRegionTile";
import { ListItemRegionTile } from "../types/list-item/ListItemRegionTile";
import { SublistRegionTile } from "../types/sublist/SublistRegionTile";
import { TableRegionTile } from "../types/table/TableRegionTile";
import { TextRegionTile } from "../types/text/TextRegionTile";
import { TOCRegionTile } from "../types/toc/TOCRegionTile";
import { UntypedRegionTile } from "../types/untyped/UntypedRegionTile";

type Props = {
  fragment: RegionTileSwitchFragment;
  children?: React.ReactNode;
  dispatch: Dispatch<EditorAction>;
};

export function RegionTileSwitch({ fragment, children, dispatch }: Props) {
  const [regionType, params] = matchRegionType(fragment);

  switch (regionType) {
    case "artifact":
      return (
        <ArtifactRegionTile
          fragment={fragment}
          paginationArtifactAttributeId={params.paginationId}
          dispatch={dispatch}
        >
          {children}
        </ArtifactRegionTile>
      );
    case "caption":
      return (
        <CaptionRegionTile
          fragment={fragment}
          textAttributeId={params.textId}
          dispatch={dispatch}
        >
          {children}
        </CaptionRegionTile>
      );
    case "figure":
      return (
        <FigureRegionTile
          fragment={fragment}
          figureAttributeId={params.figureId}
          dispatch={dispatch}
        >
          {children}
        </FigureRegionTile>
      );
    case "formula":
      return (
        <FormulaRegionTile
          fragment={fragment}
          expressionAttributeId={params.expressionId}
          dispatch={dispatch}
        >
          {children}
        </FormulaRegionTile>
      );
    case "heading":
      return (
        <HeadingRegionTile
          fragment={fragment}
          headerLevelAttributeId={params.headerLevelId}
          textAttributeId={params.textId}
          dispatch={dispatch}
        >
          {children}
        </HeadingRegionTile>
      );
    case "hybrid":
      return (
        <HybridRegionTile fragment={fragment} dispatch={dispatch}>
          {children}
        </HybridRegionTile>
      );
    case "image":
      return (
        <ImageRegionTile
          fragment={fragment}
          alternativeTextAttributeId={params.alternativeTextId}
          dispatch={dispatch}
        >
          {children}
        </ImageRegionTile>
      );
    case "list":
      return (
        <ListRegionTile
          fragment={fragment}
          listAttributeId={params.listId}
          dispatch={dispatch}
        >
          {children}
        </ListRegionTile>
      );
    case "listItem":
      return (
        <ListItemRegionTile
          fragment={fragment}
          listItemAttributeId={params.listItemId}
          textAttributeId={params.textId}
          dispatch={dispatch}
        >
          {children}
        </ListItemRegionTile>
      );
    case "sublist":
      return (
        <SublistRegionTile
          fragment={fragment}
          listItemAttributeId={params.listItemId}
          listAttributeId={params.listId}
          dispatch={dispatch}
        >
          {children}
        </SublistRegionTile>
      );
    case "table":
      return (
        <TableRegionTile
          fragment={fragment}
          tableAttributeId={params.tableId}
          dispatch={dispatch}
        >
          {children}
        </TableRegionTile>
      );
    case "text":
      return (
        <TextRegionTile
          fragment={fragment}
          textAttributeId={params.textId}
          dispatch={dispatch}
        >
          {children}
        </TextRegionTile>
      );
    case "toc":
      return (
        <TOCRegionTile
          fragment={fragment}
          tocAttributeId={params.tocId}
          dispatch={dispatch}
        >
          {children}
        </TOCRegionTile>
      );
    case "untyped":
      return (
        <UntypedRegionTile fragment={fragment} dispatch={dispatch}>
          {children}
        </UntypedRegionTile>
      );
  }
}
